import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { useWorkspace } from '../../components/Workspace/artifacts';
import type { Artifact, DocumentArtifact } from '../../components/Workspace/artifacts';
import CuriousTiptapEditor from '../../components/Editor/CuriousTiptapEditor';

const isDocumentArtifact = (artifact: Artifact | undefined): artifact is DocumentArtifact =>
  !!artifact && artifact.type === 'document';

// Empty TipTap document (blank slate)
const EMPTY_DOC = { type: 'doc', content: [] };

const EditorPage: React.FC = () => {
  const { state, createDocument, getArtifact, updateArtifact, isSyncing, openArtifact } = useWorkspace();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [localArtifactId, setLocalArtifactId] = useState<string | null>(null);
  const [hasCreatedArtifact, setHasCreatedArtifact] = useState(false);
  const [showArtifactPicker, setShowArtifactPicker] = useState(false);
  const [artifactSearch, setArtifactSearch] = useState('');
  const pickerRef = useRef<HTMLDivElement>(null);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setShowArtifactPicker(false);
      }
    };

    if (showArtifactPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showArtifactPicker]);

  const targetArtifactId = useMemo(() => {
    const fromQuery = searchParams.get('artifact');
    if (fromQuery) return fromQuery;
    return localArtifactId;
  }, [searchParams, localArtifactId]);

  const artifact = useMemo(
    () => (targetArtifactId ? getArtifact(targetArtifactId) : undefined),
    [getArtifact, targetArtifactId, state.artifacts],
  );

  // Filter document artifacts for the picker
  const documentArtifacts = useMemo(() => {
    return state.artifacts
      .filter((a): a is DocumentArtifact => a.type === 'document')
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }, [state.artifacts]);

  const filteredArtifacts = useMemo(() => {
    if (!artifactSearch.trim()) return documentArtifacts;
    const query = artifactSearch.toLowerCase();
    return documentArtifacts.filter(
      (a) =>
        a.title.toLowerCase().includes(query) ||
        (typeof a.content === 'string' && a.content.toLowerCase().includes(query)),
    );
  }, [documentArtifacts, artifactSearch]);

  const documentArtifact = isDocumentArtifact(artifact) ? artifact : undefined;
  const isSaving = documentArtifact ? isSyncing(documentArtifact.id) : false;

  // Handle first content change: create artifact if none exists
  const handleChange = useCallback(
    async (content: Record<string, unknown>) => {
      // Check if content has actual text (not just empty paragraphs)
      const hasContent = (() => {
        if (!content || typeof content !== 'object' || !Array.isArray(content.content)) {
          return false;
        }
        if (content.content.length === 0) {
          return false;
        }
        // Check if any node has actual text content
        const hasText = (node: any): boolean => {
          if (node.type === 'text' && node.text && node.text.trim().length > 0) {
            return true;
          }
          if (node.content && Array.isArray(node.content)) {
            return node.content.some(hasText);
          }
          // Non-text nodes (images, tables, etc.) count as content
          return node.type !== 'paragraph' && node.type !== 'text';
        };
        return content.content.some(hasText);
      })();

      // Non-empty content: create artifact if needed, otherwise update
      if (hasContent) {
        if (!documentArtifact && !hasCreatedArtifact) {
          setHasCreatedArtifact(true);
          try {
            const created = await createDocument('Untitled Document', '', 'user');
            setLocalArtifactId(created.id);
            openArtifact(created.id);
            setSearchParams({ artifact: created.id });
            // Update with the actual content
            updateArtifact(created.id, {
              content,
              format: 'tiptap_json' as DocumentArtifact['format'],
            });
          } catch (error) {
            console.error('Failed to create artifact:', error);
          }
        } else if (documentArtifact) {
          // Update existing artifact
          updateArtifact(documentArtifact.id, {
            content,
            format: 'tiptap_json' as DocumentArtifact['format'],
          });
        }
      } else if (documentArtifact) {
        // Empty content: still update the artifact (user might have deleted everything)
        updateArtifact(documentArtifact.id, {
          content,
          format: 'tiptap_json' as DocumentArtifact['format'],
        });
      }
      // If no artifact and no content, do nothing (blank slate)
    },
    [documentArtifact, hasCreatedArtifact, createDocument, updateArtifact, openArtifact, setSearchParams],
  );

  const handleOpenArtifact = useCallback(
    (id: string) => {
      openArtifact(id);
      setSearchParams({ artifact: id });
      setShowArtifactPicker(false);
      setArtifactSearch('');
      setHasCreatedArtifact(true); // Prevent auto-create after opening
    },
    [openArtifact, setSearchParams],
  );

  // Reset hasCreatedArtifact when artifact changes externally
  useEffect(() => {
    if (targetArtifactId && artifact) {
      setHasCreatedArtifact(true);
    }
  }, [targetArtifactId, artifact]);

  // Get current editor content (empty doc if no artifact, or artifact content)
  const editorContent = useMemo(() => {
    if (documentArtifact && documentArtifact.format === 'tiptap_json' && typeof documentArtifact.content === 'object') {
      return documentArtifact.content as Record<string, unknown>;
    }
    // Show empty doc for blank slate
    return EMPTY_DOC;
  }, [documentArtifact]);

  return (
    <div className="flex h-full flex-col gap-4 px-4 py-4 lg:px-8 lg:py-6">
      <header className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-lg font-semibold text-neutral-900">Editor</h1>
            <p className="text-xs text-neutral-500">
              A full-featured Tiptap editor, wired to your workspace artifacts.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowArtifactPicker(!showArtifactPicker)}
            className="px-3 py-1.5 text-xs font-medium text-neutral-700 bg-white border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors"
          >
            {documentArtifact ? 'Switch Document' : 'Open Document'}
          </button>
        </div>
        {documentArtifact && (
          <div className="text-xs text-neutral-500">
            <span className="font-medium text-neutral-700">{documentArtifact.title}</span>
            <span className="mx-2 text-neutral-300">•</span>
            {isSaving ? <span className="text-blue-600">Saving…</span> : <span>All changes saved</span>}
          </div>
        )}
      </header>

      {/* Artifact picker dropdown */}
      {showArtifactPicker && (
        <div ref={pickerRef} className="relative z-50">
          <div className="absolute top-0 left-0 right-0 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-96 overflow-hidden flex flex-col">
            <div className="p-3 border-b border-neutral-200">
              <input
                type="text"
                placeholder="Search documents..."
                value={artifactSearch}
                onChange={(e) => setArtifactSearch(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>
            <div className="overflow-y-auto">
              {filteredArtifacts.length === 0 ? (
                <div className="p-4 text-sm text-neutral-500 text-center">
                  {artifactSearch ? 'No documents found' : 'No documents yet'}
                </div>
              ) : (
                filteredArtifacts.map((art) => (
                  <button
                    key={art.id}
                    type="button"
                    onClick={() => handleOpenArtifact(art.id)}
                    className={`w-full px-4 py-3 text-left hover:bg-neutral-50 transition-colors border-b border-neutral-100 last:border-b-0 ${
                      art.id === documentArtifact?.id ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="font-medium text-sm text-neutral-900">{art.title}</div>
                    <div className="text-xs text-neutral-500 mt-1">
                      {new Date(art.updatedAt).toLocaleDateString()} • {art.status}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <main className="flex-1 min-h-0">
        <CuriousTiptapEditor
          value={editorContent}
          onChange={handleChange}
          className="h-full"
          imageUploadDocStem={documentArtifact?.id ?? "_uploads"}
        />
      </main>
    </div>
  );
};

export default EditorPage;

